import type { Participant } from "../types/messaging.types.ts"

/**
 * A mention travels as `@[uuid]` so the identity survives a rename, and the
 * display name is resolved at render time from the conversation's own roster.
 * Storing the name in the text would leave stale names in old messages and
 * would let anyone type a name that looks like a mention.
 */
export const MENTION_MARKER = /@\[([0-9a-fA-F-]{36})\]/g

export type MessageSegment =
  | { kind: "text"; value: string }
  | { kind: "mention"; userId: string; label: string }
  | { kind: "link"; href: string; label: string }

const URL_PATTERN = /(https?:\/\/[^\s<]+)/g

const linkSegments = (text: string): MessageSegment[] => {
  const segments: MessageSegment[] = []
  let cursor = 0
  for (const match of text.matchAll(URL_PATTERN)) {
    const start = match.index ?? 0
    if (start > cursor) {
      segments.push({ kind: "text", value: text.slice(cursor, start) })
    }
    // A trailing sentence mark is punctuation, not part of the address.
    const raw = match[0].replace(/[.,;:!?)\]]+$/, "")
    segments.push({ kind: "link", href: raw, label: raw })
    cursor = start + raw.length
  }
  if (cursor < text.length) {
    segments.push({ kind: "text", value: text.slice(cursor) })
  }
  return segments
}

export const toSegments = (
  body: string,
  participants: Participant[],
  /** Names carried by the message itself; authoritative over the roster. */
  names: Record<string, string> = {}
): MessageSegment[] => {
  const byId = new Map(participants.map((person) => [person.id, person]))
  const segments: MessageSegment[] = []
  let cursor = 0

  for (const match of body.matchAll(MENTION_MARKER)) {
    const start = match.index ?? 0
    if (start > cursor) {
      segments.push(...linkSegments(body.slice(cursor, start)))
    }
    const resolved = names[match[1]] ?? byId.get(match[1])?.full_name
    segments.push({
      kind: "mention",
      userId: match[1],
      // Someone who has since left the conversation still has to render as
      // their name — the mention happened, and "@unknown" reads as a bug.
      label: resolved ? `@${resolved}` : "@unknown"
    })
    cursor = start + match[0].length
  }
  if (cursor < body.length) {
    segments.push(...linkSegments(body.slice(cursor)))
  }
  return segments
}

/** What the composer shows while typing: the marker replaced by the name. */
export const toDisplayText = (
  body: string,
  participants: Participant[]
): string =>
  toSegments(body, participants)
    .map((segment) => (segment.kind === "text" ? segment.value : segment.label))
    .join("")

export interface MentionQuery {
  /** Index of the `@` that opened the query. */
  start: number
  term: string
}

/**
 * The partial `@name` under the caret, or null when the caret is not in one.
 * A mention opens at a word boundary only, so an email address never does.
 */
export const mentionQueryAt = (
  text: string,
  caret: number
): MentionQuery | null => {
  const upToCaret = text.slice(0, caret)
  const at = upToCaret.lastIndexOf("@")
  if (at === -1) return null
  if (at > 0 && !/\s/.test(upToCaret[at - 1])) return null
  const term = upToCaret.slice(at + 1)
  if (/[\s[\]]/.test(term)) return null
  return { start: at, term }
}

export const applyMention = (
  text: string,
  query: MentionQuery,
  caret: number,
  person: Participant
): { text: string; caret: number } => {
  const marker = `@[${person.id}] `
  const next = text.slice(0, query.start) + marker + text.slice(caret)
  return { text: next, caret: query.start + marker.length }
}
